import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useAuth from '../auth/useAuth';
import Loading from '../components/Loading';
import { listInvoices } from '../services/invoices';
import { formatCurrency, formatDateShort } from '../utils/formatters';

/**
 * Account Statements — fifth tab under /invoices. A per-customer Aged
 * Accounts Receivable statement of every invoice that's still owed,
 * mirroring the spreadsheet Proxemics kept by hand (one tab per
 * customer).
 *
 * Data model — intentionally NO new list. This is a live VIEW over the
 * existing invoices list:
 *
 *   • Outstanding = any invoice whose Status is Sent, Awaiting
 *     Payment, Partial Payment, or Overdue. The moment a Director
 *     marks an invoice Paid (or Cancelled) on the Invoice Manager tab,
 *     it drops off this statement automatically — that's the
 *     "remove it once it's paid" step, done for you.
 *
 *   • Invoices are grouped by CustomerName. Pick a customer (like the
 *     old spreadsheet tabs) to see that customer's statement.
 *
 *   • Each invoice's total lands in one aging bucket (1-30 / 31-60 /
 *     61-90 / >91 days) based on how old it is. The "Age based on"
 *     toggle decides whether age is measured from the Invoice Date or
 *     the Due Date — same toggle as the spreadsheet, defaulting to
 *     Invoice Date.
 *
 *   • "Terms" is derived from the gap between the invoice's issue and
 *     due dates (e.g. Net 60), since invoices don't store a separate
 *     terms field.
 */

// The statuses that still represent money owed. Paid / Overpaid /
// Duplicate Pay (already settled), Cancelled (voided), and Draft (not
// issued yet) are intentionally excluded so they never show on a
// statement.
const OUTSTANDING_STATUSES = new Set([
  'Sent',
  'Awaiting Payment',
  'Partial Payment',
  'Overdue',
]);

export default function AccountStatements() {
  const { isDirector } = useAuth();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  // 'issue' (Invoice Date) or 'due' (Due Date) — matches the
  // spreadsheet's "Age based on" radio. Invoice Date is the default.
  const [ageBasis, setAgeBasis] = useState('issue');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listInvoices();
      setInvoices(rows || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ---- Outstanding invoices grouped by customer ----------------------
  const byCustomer = useMemo(() => {
    const map = new Map();
    for (const inv of invoices || []) {
      if (!OUTSTANDING_STATUSES.has(inv.Status || '')) continue;
      const name = String(inv.CustomerName || '').trim() || 'Unassigned';
      if (!map.has(name)) map.set(name, []);
      map.get(name).push(inv);
    }
    return map;
  }, [invoices]);

  // Customer names that currently carry a balance, alphabetical.
  const customers = useMemo(
    () => Array.from(byCustomer.keys()).sort((a, b) => a.localeCompare(b)),
    [byCustomer],
  );

  // Keep the picker pointed at a valid customer as data loads/changes.
  useEffect(() => {
    if (customers.length === 0) {
      if (selectedCustomer !== '') setSelectedCustomer('');
      return;
    }
    if (!customers.includes(selectedCustomer)) {
      setSelectedCustomer(customers[0]);
    }
  }, [customers, selectedCustomer]);

  // ---- Statement rows for the selected customer ----------------------
  const statement = useMemo(() => {
    const rows = (byCustomer.get(selectedCustomer) || []).map((inv) => {
      const issueIso = toIsoDate(inv.IssueDate);
      const dueIso = toIsoDate(inv.DueDate);
      const total = Number(inv.Total) || 0;
      const basisIso = ageBasis === 'due' ? dueIso : issueIso;
      const age = basisIso ? daysSince(basisIso) : null;
      return {
        invoice: inv,
        issueIso,
        dueIso,
        total,
        terms: netTerms(issueIso, dueIso),
        bucket: agingBucket(age),
      };
    });

    // Oldest first so the most overdue sits at the top of the statement.
    rows.sort((a, b) => {
      const aKey = (ageBasis === 'due' ? a.dueIso : a.issueIso) || '';
      const bKey = (ageBasis === 'due' ? b.dueIso : b.issueIso) || '';
      return aKey.localeCompare(bKey);
    });

    const totals = { total: 0, b1: 0, b2: 0, b3: 0, b4: 0 };
    for (const r of rows) {
      totals.total += r.total;
      if (r.bucket === 0) totals.b1 += r.total;
      else if (r.bucket === 1) totals.b2 += r.total;
      else if (r.bucket === 2) totals.b3 += r.total;
      else if (r.bucket === 3) totals.b4 += r.total;
    }
    return { rows, totals };
  }, [byCustomer, selectedCustomer, ageBasis]);

  // ---- Render guards --------------------------------------------------
  if (!isDirector) {
    return (
      <div className="card">
        <div className="card-title">Account Statements</div>
        <p className="muted">Only Directors can view account statements.</p>
      </div>
    );
  }
  if (loading) return <Loading />;
  if (error) {
    return (
      <div className="card" style={{ borderColor: 'var(--brand-danger)' }}>
        <div className="card-title">Couldn't Load Account Statements</div>
        <p>{error.message || 'Unknown error.'}</p>
        <button className="btn btn-outline btn-sm" onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  const today = new Date();

  return (
    <>
      <div className="page-header">
        <h1>Account Statements</h1>
        <div className="actions">
          <button className="btn btn-outline" onClick={load}>
            Refresh
          </button>
        </div>
      </div>

      <p className="muted" style={{ marginTop: 0 }}>
        Outstanding invoices per customer, aged into 30-day buckets. An
        invoice appears here while its status is Sent, Awaiting Payment,
        Partial Payment, or Overdue — once you mark it Paid on the{' '}
        <Link to="/invoices/tracker">Invoice Manager</Link> tab it drops
        off this statement automatically.
      </p>

      {customers.length === 0 ? (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            No outstanding invoices right now — every issued invoice is
            either paid or cancelled. Nice.
          </p>
        </div>
      ) : (
        <>
          {/* Controls — customer picker + the "Age based on" toggle. */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
              alignItems: 'flex-end',
              marginBottom: 16,
            }}
          >
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span
                style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-muted)' }}
              >
                Customer
              </span>
              <select
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                style={{ minWidth: 240, padding: '8px 10px' }}
              >
                {customers.map((c) => {
                  const count = (byCustomer.get(c) || []).length;
                  return (
                    <option key={c} value={c}>
                      {c} ({count})
                    </option>
                  );
                })}
              </select>
            </label>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span
                style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-muted)' }}
              >
                Age based on
              </span>
              <div
                style={{
                  display: 'inline-flex',
                  border: '1px solid var(--brand-border, #e5e7eb)',
                  borderRadius: 6,
                  overflow: 'hidden',
                }}
              >
                {[
                  { key: 'issue', label: 'Invoice Date' },
                  { key: 'due', label: 'Due Date' },
                ].map((opt) => {
                  const active = ageBasis === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setAgeBasis(opt.key)}
                      style={{
                        padding: '8px 14px',
                        fontSize: 13,
                        fontWeight: 500,
                        border: 'none',
                        cursor: 'pointer',
                        background: active
                          ? 'var(--brand-primary)'
                          : 'transparent',
                        color: active ? '#fff' : 'var(--brand-muted)',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Statement header block — mirrors the spreadsheet's top
              section: who it's for, and the date the aging was run. */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div>
                <div
                  style={{ fontSize: 12, color: 'var(--brand-muted)', fontWeight: 600 }}
                >
                  Customer
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--brand-ink)' }}>
                  {selectedCustomer}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div
                  style={{ fontSize: 12, color: 'var(--brand-muted)', fontWeight: 600 }}
                >
                  Aging Date
                </div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>
                  {formatDateShort(today)}
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ padding: 0 }}>
            <table className="mini-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th style={{ textAlign: 'center' }}>Terms</th>
                  <th>Invoice Date</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Invoice Total</th>
                  <th style={{ textAlign: 'right' }}>1 – 30</th>
                  <th style={{ textAlign: 'right' }}>31 – 60</th>
                  <th style={{ textAlign: 'right' }}>61 – 90</th>
                  <th style={{ textAlign: 'right' }}>&gt; 91</th>
                </tr>
              </thead>
              <tbody>
                {statement.rows.map(({ invoice, issueIso, dueIso, total, terms, bucket }) => (
                  <tr key={invoice.id}>
                    <td>
                      <Link to={`/invoices/view/${encodeURIComponent(invoice.id)}`}>
                        {invoice.InvoiceNumber || invoice.id}
                      </Link>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {terms != null ? `Net ${terms}` : <span className="muted">—</span>}
                    </td>
                    <td>{issueIso ? formatDateShort(issueIso) : '—'}</td>
                    <td>{dueIso ? formatDateShort(dueIso) : '—'}</td>
                    <td>{invoice.Status || '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {formatCurrency(total)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {bucket === 0 ? formatCurrency(total) : ''}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {bucket === 1 ? formatCurrency(total) : ''}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {bucket === 2 ? formatCurrency(total) : ''}
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        color: bucket === 3 ? 'var(--brand-danger)' : undefined,
                        fontWeight: bucket === 3 ? 700 : 400,
                      }}
                    >
                      {bucket === 3 ? formatCurrency(total) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--brand-border, #e5e7eb)' }}>
                  <td colSpan={5} style={{ fontWeight: 700, textAlign: 'right' }}>
                    Total Accounts Receivable Aging
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>
                    {formatCurrency(statement.totals.total)}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>
                    {formatCurrency(statement.totals.b1)}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>
                    {formatCurrency(statement.totals.b2)}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>
                    {formatCurrency(statement.totals.b3)}
                  </td>
                  <td
                    style={{
                      textAlign: 'right',
                      fontWeight: 700,
                      color: statement.totals.b4 > 0 ? 'var(--brand-danger)' : undefined,
                    }}
                  >
                    {formatCurrency(statement.totals.b4)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </>
  );
}

// ---- Helpers ---------------------------------------------------------------

// Normalize a SharePoint date (may carry a time component) to
// YYYY-MM-DD for string comparison; empty string when absent.
function toIsoDate(value) {
  const m = String(value || '').match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}

// Whole days between an ISO (YYYY-MM-DD) date and today. Positive when
// the date is in the past. Compared at local midnight so the result
// doesn't drift with the time of day.
function daysSince(iso) {
  const start = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(start.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((today - start) / 86400000);
}

// Map an age in days to an aging-bucket index:
//   0 → 1-30, 1 → 31-60, 2 → 61-90, 3 → >91.
// Anything not yet 31 days old (including future-dated invoices) lands
// in the first bucket, matching the spreadsheet's four columns.
function agingBucket(age) {
  if (age == null) return 0;
  if (age <= 30) return 0;
  if (age <= 60) return 1;
  if (age <= 90) return 2;
  return 3;
}

// Net payment terms in days, derived from the gap between issue and due
// dates (the invoice record has no standalone terms field). Returns
// null when either date is missing or the math is nonsensical.
function netTerms(issueIso, dueIso) {
  if (!issueIso || !dueIso) return null;
  const issue = new Date(`${issueIso}T00:00:00`);
  const due = new Date(`${dueIso}T00:00:00`);
  if (Number.isNaN(issue.getTime()) || Number.isNaN(due.getTime())) return null;
  const days = Math.round((due - issue) / 86400000);
  return days >= 0 ? days : null;
}
